/**
 * Dashboard de Desempenho Contextual - Fase 2 HeveaTalent
 * Sistema 100% paralelo - NÃO modifica o Calendário de Sangria
 * 
 * Baseado no Tripé: Justiça, Segurança, Simplicidade
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useTranslation } from '../translations';
import ProductionContextWizard from '../components/ProductionContextWizard';
import { MetaCalculationInfo } from '../components/MetaCalculationInfo';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

const ContextualPerformanceDashboard = ({ user }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [contexts, setContexts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clones, setClones] = useState([]);
  const [tappingSystems, setTappingSystems] = useState([]);
  const [seasonalCoefficients, setSeasonalCoefficients] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('sp');
  const [showTip, setShowTip] = useState(true);
  
  // Estado para Selos (Fase 3)
  const [sealDashboard, setSealDashboard] = useState(null);
  const [sealHistory, setSealHistory] = useState([]);
  
  // Estado para o Wizard de Contexto de Produção
  const [showContextWizard, setShowContextWizard] = useState(false);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Carrega propriedades do usuário
      const propsRes = await axios.get(`${API}/api/bleeding-properties`);
      setProperties(propsRes.data);

      // Carrega contextos existentes
      const contextsRes = await axios.get(`${API}/api/contextual-performance/contexts`);
      setContexts(contextsRes.data.contexts || []);

      // Carrega dados de referência
      const [clonesRes, systemsRes, seasonalRes] = await Promise.all([
        axios.get(`${API}/api/contextual-performance/reference/clones`),
        axios.get(`${API}/api/contextual-performance/reference/tapping-systems`),
        axios.get(`${API}/api/contextual-performance/reference/seasonal-coefficients?region=sp`)
      ]);
      
      setClones(clonesRes.data.clones || []);
      setTappingSystems(systemsRes.data.systems || []);
      setSeasonalCoefficients(seasonalRes.data.coefficients || []);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  };

  const loadSeasonalForRegion = async (region) => {
    try {
      const res = await axios.get(`${API}/api/contextual-performance/reference/seasonal-coefficients?region=${region}`);
      setSeasonalCoefficients(res.data.coefficients || []);
      setSelectedRegion(region);
    } catch (error) {
      console.error('Erro ao carregar coeficientes:', error);
    }
  };

  // Carrega dados de selos do trabalhador
  const loadSealData = async () => {
    try {
      const [dashboardRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/seals/dashboard`),
        axios.get(`${API}/api/seals/my-seals?limit=12`)
      ]);
      setSealDashboard(dashboardRes.data);
      setSealHistory(historyRes.data?.history?.seals || []);
    } catch (error) {
      console.error('Erro ao carregar selos:', error);
    }
  };

  // Estado para mês/ano selecionado
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const loadPerformance = async (propertyId, month = selectedMonth, year = selectedYear) => {
    if (!propertyId) return;
    
    setLoading(true);
    try {
      const [perfRes, histRes] = await Promise.all([
        axios.get(`${API}/api/contextual-performance/calculate/${propertyId}?month=${month}&year=${year}`),
        axios.get(`${API}/api/contextual-performance/history/${propertyId}?months=12`)
      ]);
      
      setPerformance(perfRes.data);
      setHistory(histRes.data.history || []);
    } catch (error) {
      console.error('Erro ao calcular desempenho:', error);
      setPerformance(null);
    }
    setLoading(false);
  };

  const handleMonthYearChange = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    if (selectedProperty) {
      loadPerformance(selectedProperty, month, year);
    }
  };

  // Carrega dados iniciais
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        const propsRes = await axios.get(`${API}/api/bleeding-properties`);
        setProperties(propsRes.data);

        const contextsRes = await axios.get(`${API}/api/contextual-performance/contexts`);
        setContexts(contextsRes.data.contexts || []);

        const [clonesRes, systemsRes, seasonalRes] = await Promise.all([
          axios.get(`${API}/api/contextual-performance/reference/clones`),
          axios.get(`${API}/api/contextual-performance/reference/tapping-systems`),
          axios.get(`${API}/api/contextual-performance/reference/seasonal-coefficients?region=sp`)
        ]);
        
        setClones(clonesRes.data.clones || []);
        setTappingSystems(systemsRes.data.systems || []);
        setSeasonalCoefficients(seasonalRes.data.coefficients || []);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
      setLoading(false);
    };
    initData();
  }, []);

  return (
    <div className="space-y-6">
      {/* Guia Explicativo do Sistema de Avaliação */}
      {showTip && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-teal-400 hover:text-teal-600"
            data-testid="close-guide-btn"
          >
            ✕
          </button>
          <div className="pr-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📊</span>
              <h4 className="font-semibold text-teal-800">{t('howEvaluationWorks')}</h4>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 text-sm text-teal-700">
              {/* Coluna 1: Passos */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-teal-600 bg-teal-200 rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                  <div>
                    <strong>{t('cpConfigureContext')}</strong> (⚙️): {t('cpConfigContextDesc')}
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <span className="font-bold text-teal-600 bg-teal-200 rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                  <div>
                    <strong>{t('cpRegisterCalendar')}</strong>: {t('cpRegisterCalendarDesc')}
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <span className="font-bold text-teal-600 bg-teal-200 rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                  <div>
                    <strong>{t('cpTechnicalEval')}</strong> {t('cpTechnicalEvalDesc')}
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <span className="font-bold text-teal-600 bg-teal-200 rounded-full w-5 h-5 flex items-center justify-center text-xs">4</span>
                  <div>
                    <strong>{t('cpAutoSeal')}</strong>: {t('cpAutoSealDesc')}
                  </div>
                </div>
              </div>
              
              {/* Coluna 2: Níveis e Info */}
              <div className="space-y-2">
                <div className="p-2 bg-teal-100 rounded">
                  <strong className="block mb-1">{t('sealLevels')}:</strong>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">🥉 Bronze 60-74 pts</span>
                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">🥈 Prata 75-89 pts</span>
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">🥇 Ouro 90-100 pts</span>
                  </div>
                </div>
                
                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                  <strong>{t('cpTechnicalEval14')}</strong>
                  <ul className="mt-1 space-y-0.5 text-amber-800">
                    <li>• Grupo A: Técnica de sangria (45 pts)</li>
                    <li>• Grupo B1: Preparação de safra (20 pts)</li>
                    <li>• Grupo B2: Manutenção diária (15 pts)</li>
                    <li>• Grupo C: Aplicações químicas (20 pts)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-2">
          <span className="text-3xl">📊</span>
          <h1 className="text-2xl font-bold">{t('contextualPerformance')}</h1>
        </div>
        <p className="text-emerald-100">
          Compare sua produção com metas justas baseadas no seu contexto específico
        </p>
        
        {/* Tripé Badge */}
        <div className="flex flex-wrap gap-2 mt-4">
          <Badge className="bg-white/20 text-white">⚖️ Justiça</Badge>
          <Badge className="bg-white/20 text-white">🔒 Segurança</Badge>
          <Badge className="bg-white/20 text-white">✨ Simplicidade</Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b overflow-x-auto [&>button]:whitespace-nowrap">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'dashboard' 
              ? 'border-emerald-500 text-emerald-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📊 Dashboard
        </button>
        <button
          onClick={() => setActiveTab('contexts')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'contexts' 
              ? 'border-emerald-500 text-emerald-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          ⚙️ Configurar Contextos
        </button>
        <button
          onClick={() => setActiveTab('reference')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'reference' 
              ? 'border-emerald-500 text-emerald-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📚 Tabela de Metas
        </button>
        <button
          onClick={() => { setActiveTab('seals'); loadSealData(); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'seals' 
              ? 'border-yellow-500 text-yellow-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🏆 Meus Selos
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && (
        <PerformanceDashboard 
          properties={properties}
          contexts={contexts}
          performance={performance}
          history={history}
          loading={loading}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
          loadPerformance={loadPerformance}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthYearChange={handleMonthYearChange}
        />
      )}

      {activeTab === 'contexts' && (
        <div className="space-y-4">
          {/* Botão de Wizard */}
          <div className="flex justify-end">
            <Button 
              onClick={() => setShowContextWizard(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="open-wizard-btn"
            >
              ✨ {t('wizardUseGuided') || 'Usar Assistente Guiado'}
            </Button>
          </div>
          
          <ContextManager 
            properties={properties}
            contexts={contexts}
            clones={clones}
            tappingSystems={tappingSystems}
            onContextCreated={loadInitialData}
          />
        </div>
      )}

      {activeTab === 'reference' && (
        <ReferenceTable 
          clones={clones}
          tappingSystems={tappingSystems}
          seasonalCoefficients={seasonalCoefficients}
          selectedRegion={selectedRegion}
          onRegionChange={loadSeasonalForRegion}
        />
      )}

      {activeTab === 'seals' && (
        <SealsDashboard 
          sealDashboard={sealDashboard}
          sealHistory={sealHistory}
          loading={loading}
          onRefresh={loadSealData}
          properties={properties}
          contexts={contexts}
          selectedProperty={selectedProperty}
          user={user}
        />
      )}

      {/* Wizard de Contexto de Produção */}
      <ProductionContextWizard
        isOpen={showContextWizard}
        properties={properties}
        contexts={contexts}
        clones={clones}
        tappingSystems={tappingSystems}
        onComplete={() => {
          setShowContextWizard(false);
          loadInitialData();
        }}
        onCancel={() => setShowContextWizard(false)}
      />
    </div>
  );
};

// =============================================================================
// DASHBOARD DE PERFORMANCE
// =============================================================================

const PerformanceDashboard = ({ 
  properties, contexts, performance, history, loading,
  selectedProperty, setSelectedProperty, loadPerformance,
  selectedMonth, selectedYear, onMonthYearChange
}) => {
  const { t } = useTranslation();
  const handlePropertyChange = (propertyId) => {
    setSelectedProperty(propertyId);
    loadPerformance(propertyId);
  };

  // Verifica se a propriedade tem contexto configurado
  const hasContext = (propertyId) => {
    return contexts.some(c => c.property_id === propertyId);
  };

  // Lista de meses para seleção
  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  // Anos disponíveis (últimos 3 anos)
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="space-y-6">
      {/* Seletor de Propriedade e Período */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Filtros de Análise</CardTitle>
          <CardDescription>
            Selecione a propriedade e o período para analisar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {/* Propriedade */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t('cpPropertyLabel')}</label>
              <Select value={selectedProperty || ''} onValueChange={handlePropertyChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.name} {hasContext(prop.id) ? '✅' : '⚠️'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mês */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t('cpMonthLabel')}</label>
              <Select 
                value={String(selectedMonth)} 
                onValueChange={(v) => onMonthYearChange(parseInt(v), selectedYear)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m.value} value={String(m.value)}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ano */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t('cpYearLabel')}</label>
              <Select 
                value={String(selectedYear)} 
                onValueChange={(v) => onMonthYearChange(selectedMonth, parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {selectedProperty && !hasContext(selectedProperty) && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800">
                ⚠️ Esta propriedade não tem um contexto configurado. 
                Vá para a aba &quot;Configurar Contextos&quot; para definir clone, sistema de sangria e outras configurações.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      )}

      {/* Performance Card */}
      {performance && !loading && (
        <div className="grid md:grid-cols-3 gap-6">
          {/* Card Principal */}
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <span className="text-4xl">{performance.performance?.emoji}</span>
                    <span>{performance.performance?.classification}</span>
                  </CardTitle>
                  <CardDescription>
                    {performance.context?.property_name} - {getMonthName(performance.month)}/{performance.year}
                  </CardDescription>
                </div>
                <Badge 
                  className={`text-lg px-4 py-2 ${getPerformanceColor(performance.performance?.percentage)}`}
                >
                  {performance.performance?.percentage}%
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Barra de Progresso */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span data-testid="cp-your-avg">{t('cpYourAvg')}: {performance.production?.avg_grams_per_cut}{t('cpGramsPerTreeCut')} <span className="text-gray-400">(seca)</span></span>
                  <span className="inline-flex items-center gap-1">{t('cpGoal')}: {performance.goal?.target_grams}{t('cpGramsPerTreeCut')}
                    <MetaCalculationInfo
                      goal={performance.goal}
                      context={performance.context}
                      drc={performance.production?.drc_percentage}
                      wetGrams={performance.production?.avg_grams_per_cut_wet}
                      dryGrams={performance.production?.avg_grams_per_cut}
                    />
                  </span>
                </div>
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${getProgressBarColor(performance.performance?.percentage)}`}
                    style={{ width: `${Math.min(performance.performance?.percentage || 0, 120)}%` }}
                  />
                </div>
                {performance.production?.avg_grams_per_cut_wet > 0 && performance.production?.avg_grams_per_cut_wet !== performance.production?.avg_grams_per_cut && (
                  <p className="text-xs text-gray-500 mt-2" data-testid="cp-drc-note">
                    🧪 {performance.production.avg_grams_per_cut_wet}g de coágulo úmido × DRC {performance.production?.drc_percentage || 53}% = {performance.production.avg_grams_per_cut}g de borracha seca (mesma unidade da meta)
                  </p>
                )}
              </div>

              {/* Métricas */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-600">
                    {performance.production?.total_kg}
                  </div>
                  <div className="text-sm text-gray-600">kg Total</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {performance.production?.total_cuts}
                  </div>
                  <div className="text-sm text-gray-600">{t('cpCuts')}</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600" data-testid="cp-avg-dry">
                    {performance.production?.avg_grams_per_cut}g
                  </div>
                  <div className="text-sm text-gray-600">{t('cpAvgPerTreeCut')} <span className="text-gray-400">(seca)</span></div>
                </div>
              </div>

              {/* Tendência */}
              {performance.trend && (
                <div className="flex items-center space-x-2 mb-4 p-3 bg-blue-50 rounded-lg">
                  <span className="text-xl">{performance.trend.emoji}</span>
                  <span className="text-blue-800">
                    {performance.trend.difference > 0 ? '+' : ''}
                    {performance.trend.difference}% vs. {getMonthName(performance.trend.previous_month)}
                  </span>
                </div>
              )}

              {/* Feedback */}
              {performance.feedback && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-emerald-800">{performance.feedback}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card de Contexto */}
          <Card>
            <CardHeader>
              <CardTitle>ℹ️ Seu Contexto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cpClone')}:</span>
                <span className="font-medium">{performance.context?.clone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cpSystem')}:</span>
                <span className="font-medium">{performance.context?.system}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cpStimulation')}:</span>
                <span className="font-medium">
                  {performance.context?.uses_stimulation 
                    ? `Sim (${performance.context?.stimulation_pct}%)` 
                    : 'Não'}
                </span>
              </div>
              
              <hr className="my-4" />
              
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cpBaseGoal')}:</span>
                <span className="font-medium">{performance.goal?.base_grams}g</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cpSeasonalCoef')}:</span>
                <span className="font-medium">×{performance.goal?.seasonal_coefficient}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cpPeriod')}:</span>
                <Badge variant="outline">{performance.goal?.seasonal_description}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Histórico com Gráfico de Barras */}
      {history.length > 0 && !loading && (
        <Card>
          <CardHeader>
            <CardTitle>📈 Histórico de Desempenho (Últimos 12 meses)</CardTitle>
            <CardDescription>
              Clique em um mês para ver detalhes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Gráfico de Barras Visual */}
            <div className="space-y-3">
              {history.slice().reverse().map((h, idx) => {
                const isSelected = h.month === selectedMonth && h.year === selectedYear;
                return (
                  <div 
                    key={idx} 
                    className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-all ${
                      isSelected ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => onMonthYearChange(h.month, h.year)}
                  >
                    {/* Mês/Ano */}
                    <div className="w-20 text-sm font-medium text-gray-700">
                      {getMonthName(h.month).substring(0, 3)}/{h.year}
                    </div>
                    
                    {/* Barra de Progresso */}
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                      {/* Linha de meta (100%) */}
                      <div 
                        className="absolute top-0 bottom-0 w-0.5 bg-gray-400 z-10"
                        style={{ left: `${Math.min(100 / 1.2, 100)}%` }}
                      />
                      {/* Barra de desempenho */}
                      <div 
                        className={`h-full transition-all duration-500 ${getProgressBarColor(h.percentage)}`}
                        style={{ width: `${Math.min(h.percentage / 1.2, 100)}%` }}
                      />
                    </div>
                    
                    {/* Porcentagem */}
                    <div className={`w-16 text-right font-bold ${
                      h.percentage >= 100 ? 'text-green-600' : 
                      h.percentage >= 70 ? 'text-blue-600' : 'text-orange-600'
                    }`}>
                      {h.percentage}%
                    </div>
                    
                    {/* Emoji */}
                    <div className="w-8 text-lg">{h.emoji}</div>
                  </div>
                );
              })}
            </div>
            
            {/* Legenda */}
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                <span>{t('cpExcellent')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>{t('cpReachedGoal')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>{t('cpGood')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-400 rounded"></div>
                <span>{t('cpRegular')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-0.5 h-3 bg-gray-400"></div>
                <span>{t('cpGoalLine')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sem dados */}
      {!performance && !loading && selectedProperty && hasContext(selectedProperty) && (
        <Card>
          <CardContent className="text-center py-8">
            <span className="text-4xl mb-4 block">📭</span>
            <p className="text-gray-600">
              Não há dados de produção registrados para este período.
              Registre sangrias no Calendário para ver seu desempenho.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// =============================================================================
// GERENCIADOR DE CONTEXTOS
// =============================================================================

const ContextManager = ({ properties, contexts, clones, tappingSystems, onContextCreated }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    property_id: '',
    property_name: '',
    clone_type: 'RRIM 600',
    tapping_system: 'D/4',
    tree_density: 450,
    trees_in_production: 0,
    uses_stimulation: false,
    stimulation_percentage: null,
    seringal_age_years: 7,
    region: 'sp',
    drc_percentage: 53
  });
  const [saving, setSaving] = useState(false);

  const handlePropertySelect = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    setFormData({
      ...formData,
      property_id: propertyId,
      property_name: property?.name || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.post(`${API}/api/contextual-performance/contexts`, formData);
      alert(t('cpContextCreated'));
      onContextCreated();
      setFormData({
        property_id: '',
        property_name: '',
        clone_type: 'RRIM 600',
        tapping_system: 'D/4',
        tree_density: 450,
        trees_in_production: 0,
        uses_stimulation: false,
        stimulation_percentage: null,
        seringal_age_years: 7,
        region: 'sudeste',
        drc_percentage: 53
      });
    } catch (error) {
      alert(t('cpErrorCreateContext') + ': ' + (error.response?.data?.detail || error.message));
    }
    
    setSaving(false);
  };

  // Propriedades sem contexto
  const propertiesWithoutContext = properties.filter(
    p => !contexts.some(c => c.property_id === p.id)
  );

  return (
    <div className="space-y-6">
      {/* Lista de Contextos Existentes */}
      {contexts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>✅ Contextos Configurados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {contexts.map(ctx => (
                <div key={ctx.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">{ctx.property_name}</div>
                    <div className="text-sm text-gray-600">
                      {ctx.clone_type} • {ctx.tapping_system} • 
                      {ctx.uses_stimulation ? ` Estimulação ${ctx.stimulation_percentage}%` : ' Sem estimulação'}
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-green-50 text-green-700">
                    Ativo
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulário para Novo Contexto */}
      <Card>
        <CardHeader>
          <CardTitle>➕ Configurar Novo Contexto</CardTitle>
          <CardDescription>
            Defina as características da propriedade para calcular metas personalizadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Info Box */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>{t('cpWhyConfigContext')}</strong>
            <p className="mt-1">O contexto define sua <strong>meta justa</strong> baseada no clone, sistema de sangria e número de árvores. Sem contexto, não é possível calcular o desempenho contextual para o selo.</p>
          </div>
          
          {propertiesWithoutContext.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              <span className="text-4xl mb-4 block">✅</span>
              <p>{t('cpAllConfigured')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Propriedade */}
              <div>
                <Label>{t('cpPropertyLabel')}</Label>
                <Select value={formData.property_id} onValueChange={handlePropertySelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a propriedade..." />
                  </SelectTrigger>
                  <SelectContent>
                    {propertiesWithoutContext.map(prop => (
                      <SelectItem key={prop.id} value={prop.id}>
                        {prop.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Clone */}
                <div>
                  <Label>{t('cpCloneType')}</Label>
                  <Select 
                    value={formData.clone_type} 
                    onValueChange={(v) => setFormData({...formData, clone_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {clones.map(clone => (
                        <SelectItem key={clone.name} value={clone.name}>
                          {clone.name} ({clone.grams_per_cut}g/corte)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sistema de Sangria */}
                <div>
                  <Label>{t('cpTappingSystem')}</Label>
                  <Select 
                    value={formData.tapping_system} 
                    onValueChange={(v) => setFormData({...formData, tapping_system: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tappingSystems.map(sys => (
                        <SelectItem key={sys.name} value={sys.name}>
                          {sys.name} - {sys.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {/* Densidade */}
                <div>
                  <Label>{t('cpTreesPerHectare')}</Label>
                  <Input 
                    type="number"
                    value={formData.tree_density}
                    onChange={(e) => setFormData({...formData, tree_density: parseInt(e.target.value) || 450})}
                  />
                </div>

                {/* Árvores em Produção */}
                <div>
                  <Label>{t('cpTotalInProduction')}</Label>
                  <Input 
                    type="number"
                    value={formData.trees_in_production}
                    onChange={(e) => setFormData({...formData, trees_in_production: parseInt(e.target.value) || 0})}
                  />
                </div>

                {/* Idade */}
                <div>
                  <Label>{t('cpPlantationAge')}</Label>
                  <Input 
                    type="number"
                    value={formData.seringal_age_years}
                    onChange={(e) => setFormData({...formData, seringal_age_years: parseInt(e.target.value) || 7})}
                  />
                </div>
              </div>

              {/* DRC — Teor de Borracha Seca */}
              <div className="p-4 border rounded-lg bg-amber-50">
                <Label className="mb-1 block">🧪 DRC — Teor de Borracha Seca (%)</Label>
                <Input
                  type="number"
                  min="30"
                  max="100"
                  step="0.5"
                  value={formData.drc_percentage ?? 53}
                  onChange={(e) => setFormData({...formData, drc_percentage: parseFloat(e.target.value) || 53})}
                  className="w-32"
                  data-testid="drc-percentage-input"
                />
                <p className="text-xs text-amber-700 mt-2">
                  Converte o coágulo úmido (pesado na entrega) em borracha seca para comparar com as metas dos clones.
                  Padrão de mercado (CVG): <strong>53%</strong>. Confira nos recibos da sua usina: seca creditada ÷ peso úmido entregue.
                </p>
              </div>

              {/* Região */}
              <div className="p-4 border rounded-lg bg-blue-50">
                <Label className="mb-2 block">🗺️ Região (para coeficientes sazonais)</Label>
                <Select 
                  value={formData.region} 
                  onValueChange={(v) => setFormData({...formData, region: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sp">{t('cpRegionSP')}</SelectItem>
                    <SelectItem value="es">{t('cpRegionES')}</SelectItem>
                    <SelectItem value="mt">{t('cpRegionMT')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-700 mt-2">
                  ⚠️ A região afeta os coeficientes sazonais. Em SP, MAIO é pico (inverno frio favorece).
                </p>
              </div>

              {/* Estimulação */}
              <div className="p-4 border rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t('cpUsesStimulation')}</Label>
                    <p className="text-sm text-gray-500">{t('cpIncreasesProduction')}</p>
                  </div>
                  <Switch 
                    checked={formData.uses_stimulation}
                    onCheckedChange={(v) => setFormData({
                      ...formData, 
                      uses_stimulation: v,
                      stimulation_percentage: v ? 2.5 : null
                    })}
                  />
                </div>

                {formData.uses_stimulation && (
                  <div>
                    <Label>{t('cpConcentration')}</Label>
                    <Select 
                      value={String(formData.stimulation_percentage || 2.5)}
                      onValueChange={(v) => setFormData({...formData, stimulation_percentage: parseFloat(v)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2.5">2.5% (Baixo risco)</SelectItem>
                        <SelectItem value="3.3">3.3% (Moderado)</SelectItem>
                        <SelectItem value="5.0">5.0% (Alto risco)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Button type="submit" disabled={saving || !formData.property_id} className="w-full">
                {saving ? t('savingLabel') : t('cpSaveContext')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// TABELA DE REFERÊNCIA
// =============================================================================

const ReferenceTable = ({ clones, tappingSystems, seasonalCoefficients, selectedRegion, onRegionChange }) => {
  const { t } = useTranslation();
  const [activeRefTab, setActiveRefTab] = useState('clones');

  // Regiões disponíveis
  const regions = [
    { value: 'sp', label: 'São Paulo (Noroeste Paulista)', description: 'MAIO é PICO (inverno frio)' },
    { value: 'es', label: 'Espírito Santo / Bahia', description: 'Verão é alta produção' },
    { value: 'mt', label: 'Centro-Oeste (MT/GO/MS)', description: 'Seca favorece produção' },
  ];

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">📚</span>
            <div>
              <h3 className="font-semibold text-blue-800">{t('cpOfficialGoalsTable')}</h3>
              <p className="text-blue-700 text-sm">
                Dados consolidados de literatura técnica brasileira (INCAPER, CATI/SP, IAC).
                <strong> Os coeficientes sazonais variam por região!</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sub-tabs */}
      <div className="flex space-x-2 border-b overflow-x-auto [&>button]:whitespace-nowrap">
        <button
          onClick={() => setActiveRefTab('clones')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeRefTab === 'clones' ? 'border-emerald-500 text-emerald-600' : 'border-transparent'
          }`}
        >
          🌱 Clones
        </button>
        <button
          onClick={() => setActiveRefTab('systems')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeRefTab === 'systems' ? 'border-emerald-500 text-emerald-600' : 'border-transparent'
          }`}
        >
          🔪 Sistemas de Sangria
        </button>
        <button
          onClick={() => setActiveRefTab('seasonal')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeRefTab === 'seasonal' ? 'border-emerald-500 text-emerald-600' : 'border-transparent'
          }`}
        >
          📅 Sazonalidade
        </button>
      </div>

      {/* Clones Table */}
      {activeRefTab === 'clones' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('cpProductivityByClone')}</CardTitle>
            <CardDescription>
              Média em kg/ha/ano e g/corte (base: 450 árv/ha, sistema D/4)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Clone</th>
                    <th className="text-right py-2">kg/ha/ano</th>
                    <th className="text-right py-2">g/corte</th>
                    <th className="text-right py-2">vs RRIM 600</th>
                  </tr>
                </thead>
                <tbody>
                  {clones.map(clone => (
                    <tr key={clone.name} className="border-b hover:bg-gray-50">
                      <td className="py-2 font-medium">{clone.name}</td>
                      <td className="text-right">{clone.productivity_kg_ha_year}</td>
                      <td className="text-right">{clone.grams_per_cut}g</td>
                      <td className="text-right">
                        {clone.vs_rrim600 > 0 ? (
                          <span className="text-green-600">+{clone.vs_rrim600}%</span>
                        ) : (
                          <span className="text-gray-500">Ref.</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tapping Systems Table */}
      {activeRefTab === 'systems' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('cpTappingSystems')}</CardTitle>
            <CardDescription>
              Frequência de cortes e fator de ajuste de produção
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tappingSystems.map(sys => (
                <div key={sys.name} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-bold text-lg">{sys.name}</div>
                    <div className="text-sm text-gray-600">{sys.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-emerald-600">{sys.cuts_per_year} cortes/ano</div>
                    <div className="text-sm text-gray-500">{t('cpFactor')}: ×{sys.factor}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seasonal Coefficients Table */}
      {activeRefTab === 'seasonal' && (
        <Card>
          <CardHeader>
            <CardTitle>🗺️ Coeficientes Sazonais por Região</CardTitle>
            <CardDescription>
              A sazonalidade varia conforme a região. Selecione sua região para ver os coeficientes corretos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Seletor de Região */}
            <div className="mb-6">
              <Label className="mb-2 block">{t('cpSelectRegion')}</Label>
              <div className="grid md:grid-cols-3 gap-3">
                {regions.map(region => (
                  <button
                    key={region.value}
                    onClick={() => onRegionChange(region.value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedRegion === region.value 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    <div className="font-semibold">{region.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{region.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Coeficientes por Mês */}
            <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
              {seasonalCoefficients.map(coef => (
                <div 
                  key={coef.month} 
                  className={`text-center p-3 rounded-lg ${getSeasonalBgColor(coef.status)}`}
                >
                  <div className="font-bold">{getMonthName(coef.month).substring(0, 3)}</div>
                  <div className="text-lg font-bold">×{coef.coefficient}</div>
                  <div className="text-xs">{coef.status}</div>
                </div>
              ))}
            </div>
            
            {/* Info sobre região atual */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <strong>📍 {regions.find(r => r.value === selectedRegion)?.label}:</strong>{' '}
              {selectedRegion === 'sp' && 'No noroeste paulista, o inverno frio (Abril-Junho) favorece maior produção de látex. A parada ocorre em Agosto durante a senescência foliar.'}
              {selectedRegion === 'es' && 'No ES/BA, o verão é o período de maior produção. A senescência ocorre mais cedo (Junho-Julho).'}
              {selectedRegion === 'mt' && 'No Centro-Oeste, a estação seca (Abril-Junho) favorece a sangria. As chuvas no verão dificultam a coleta.'}
            </div>
            
            {/* Legenda */}
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-100 rounded"></div>
                <span>{t('cpHighPeak')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-100 rounded"></div>
                <span>{t('cpNormal')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-orange-100 rounded"></div>
                <span>{t('cpLowRecovery')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-100 rounded"></div>
                <span>{t('cpStopped')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// =============================================================================
// DASHBOARD DE SELOS (FASE 3)
// =============================================================================

const SealsDashboard = ({ sealDashboard, sealHistory, loading, onRefresh, properties, contexts, selectedProperty, user }) => {
  const { t } = useTranslation();
  const [checkingMySeal, setCheckingMySeal] = useState(false);
  const [mySealResult, setMySealResult] = useState(null);
  const [selectedPropForSeal, setSelectedPropForSeal] = useState(selectedProperty?.id || '');

  // Verificar meu selo atual baseado nos dados do calendário
  const checkMySeal = async () => {
    if (!selectedPropForSeal) {
      alert(t('cpSelectPropertyFirst'));
      return;
    }
    
    setCheckingMySeal(true);
    try {
      const now = new Date();
      const response = await axios.get(
        `${API}/api/seals/calculate?property_id=${selectedPropForSeal}&month=${now.getMonth() + 1}&year=${now.getFullYear()}`
      );
      setMySealResult(response.data);
    } catch (error) {
      console.error('Erro ao verificar selo:', error);
      setMySealResult({ error: 'Não foi possível calcular o selo. Verifique se há dados de produção e contexto configurado.' });
    }
    setCheckingMySeal(false);
  };

  // Propriedades com contexto configurado
  const propsWithContext = properties?.filter(p => 
    contexts?.some(c => c.property_id === p.id)
  ) || [];

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  const getSealGradient = (level) => {
    switch (level) {
      case 'gold': return 'from-yellow-400 to-yellow-600';
      case 'silver': return 'from-gray-300 to-gray-500';
      case 'bronze': return 'from-orange-400 to-orange-600';
      default: return 'from-gray-200 to-gray-400';
    }
  };

  const getSealBg = (level) => {
    switch (level) {
      case 'gold': return 'bg-yellow-100 border-yellow-300';
      case 'silver': return 'bg-gray-100 border-gray-300';
      case 'bronze': return 'bg-orange-100 border-orange-300';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getSealInfo = (level) => {
    switch (level) {
      case 'gold': return { emoji: '🥇', label: 'Ouro', title: 'Sangrador Expert', description: '90-100 pontos' };
      case 'silver': return { emoji: '🥈', label: 'Prata', title: 'Sangrador Profissional', description: '75-89 pontos' };
      case 'bronze': return { emoji: '🥉', label: 'Bronze', title: 'Sangrador Certificado', description: '60-74 pontos' };
      default: return { emoji: '📊', label: 'Em desenvolvimento', title: 'Em desenvolvimento', description: 'Continue trabalhando para conquistar seu selo!' };
    }
  };

  return (
    <div className="space-y-6">
      {/* Explicação do Sistema de Selos */}
      <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div className="text-sm">
              <p className="font-medium text-amber-800 mb-2">{t('cpHowSealsWork')}</p>
              <p className="text-amber-700">
                Seu selo é calculado <strong>automaticamente</strong> combinando sua avaliação técnica (14 critérios) 
                com seu desempenho contextual (produção vs meta). 
                Se você tem ambos: <strong>50% técnico + 50% contextual</strong>. 
                Se só tem avaliação técnica: <strong>100% técnico</strong>.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">🥉 Bronze 60-74 pts</span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">🥈 Prata 75-89 pts</span>
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">🥇 Ouro 90-100 pts</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selo Atual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span className="text-3xl">🏆</span>
            <span>{t('cpYourCurrentSeal')}</span>
          </CardTitle>
          <CardDescription>
            Baseado no seu desempenho do mês atual
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sealDashboard ? (
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Selo Visual */}
              <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${getSealGradient(sealDashboard.current_seal?.level)} flex items-center justify-center shadow-lg`}>
                <span className="text-5xl">{sealDashboard.current_seal?.info?.emoji}</span>
              </div>
              
              {/* Info do Selo */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold mb-2">
                  {sealDashboard.current_seal?.info?.label || 'Calculando...'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {sealDashboard.current_seal?.info?.description}
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <Badge className="bg-emerald-100 text-emerald-800">
                    Score: {sealDashboard.current_seal?.score?.toFixed(1)}%
                  </Badge>
                  {sealDashboard.next_goal && (
                    <Badge className="bg-blue-100 text-blue-800">
                      Faltam {sealDashboard.next_goal.needed?.toFixed(1)} pts para {
                        {bronze: 'Bronze', silver: 'Prata', gold: 'Ouro'}[sealDashboard.next_goal.level] || sealDashboard.next_goal.level
                      }
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <span className="text-4xl mb-4 block">📊</span>
              <p>{t('cpLoadingSealInfo')}</p>
              <Button onClick={onRefresh} variant="outline" className="mt-4">
                Atualizar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Feedback e Próximo Objetivo */}
      {sealDashboard?.feedback && (
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="py-4">
            <p className="text-gray-700">{sealDashboard.feedback}</p>
          </CardContent>
        </Card>
      )}

      {/* Estatísticas e Badges */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Resumo de Selos */}
        <Card>
          <CardHeader>
            <CardTitle>📈 Resumo de Conquistas</CardTitle>
          </CardHeader>
          <CardContent>
            {sealDashboard?.history_summary ? (
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {sealDashboard.history_summary.gold_count || 0}
                  </div>
                  <div className="text-sm text-gray-600">🥇 Ouro</div>
                  <div className="text-xs text-gray-400">{t('cpExpertTapper')}</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">
                    {sealDashboard.history_summary.silver_count || 0}
                  </div>
                  <div className="text-sm text-gray-600">🥈 Prata</div>
                  <div className="text-xs text-gray-400">{t('cpProfessionalTapper')}</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {sealDashboard.history_summary.bronze_count || 0}
                  </div>
                  <div className="text-sm text-gray-600">🥉 Bronze</div>
                  <div className="text-xs text-gray-400">{t('cpCertifiedTapper')}</div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">{t('cpNoSealYet')}</p>
            )}
            
            {sealDashboard?.history_summary?.total_seals > 0 && (
              <div className="mt-4 pt-4 border-t text-center">
                <span className="text-lg font-semibold text-emerald-600">
                  {sealDashboard.history_summary.total_seals} selos conquistados!
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Badges */}
        <Card>
          <CardHeader>
            <CardTitle>🎖️ Badges Conquistados</CardTitle>
          </CardHeader>
          <CardContent>
            {sealDashboard?.badges && sealDashboard.badges.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {sealDashboard.badges.map((badge, idx) => {
                  const badgeLabels = {
                    'welcome': 'Bem-vindo',
                    'first_seal': 'Primeiro Selo',
                    'certified': 'Certificado',
                    'seal_highlight': 'Destaque (Ouro+)'
                  };
                  return (
                    <Badge key={idx} className="bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 px-3 py-1">
                      {badgeLabels[badge] || badge}
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <span className="text-3xl mb-2 block">🌟</span>
                <p className="text-gray-500">{t('cpKeepEvolving')}</p>
                <p className="text-sm text-gray-400 mt-2">
                  Conquiste seu primeiro selo para ganhar o badge &quot;Primeiro Selo&quot;
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Histórico de Selos */}
      {sealHistory && sealHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>📜 Histórico de Selos</CardTitle>
            <CardDescription>{t('cpLast12Seals')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sealHistory.map((seal, idx) => (
                <div 
                  key={seal.id || idx}
                  className={`flex items-center justify-between p-3 rounded-lg border ${getSealBg(seal.seal_level)}`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">
                      {seal.seal_level === 'gold' ? '🥇' :
                       seal.seal_level === 'silver' ? '🥈' :
                       seal.seal_level === 'bronze' ? '🥉' : '📈'}
                    </span>
                    <div>
                      <div className="font-medium">
                        {seal.seal_level === 'gold' ? 'Ouro - Sangrador Expert' :
                         seal.seal_level === 'silver' ? 'Prata - Sangrador Profissional' :
                         seal.seal_level === 'bronze' ? 'Bronze - Sangrador Certificado' : 'Em desenvolvimento'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {getMonthName(seal.period_month)}/{seal.period_year}
                        {seal.property_name && ` - ${seal.property_name}`}
                      </div>
                      <div className="text-xs text-gray-400">
                        {seal.seal_category === 'technical' ? 'Avaliação 100% Técnica' :
                         seal.seal_category === 'combined' ? '50% Técnico + 50% Contextual' :
                         seal.seal_category === 'contextual' ? '100% Contextual' : seal.seal_category}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{seal.combined_score?.toFixed(1)} pts</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info sobre Thresholds */}
      <Card className="bg-gradient-to-r from-gray-50 to-gray-100">
        <CardHeader>
          <CardTitle>📋 Como Funcionam os Selos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3">{t('cpSealLevels')}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                  <span>🥇 Ouro - Sangrador Expert</span>
                  <span className="font-mono">90-100 pts</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span>🥈 Prata - Sangrador Profissional</span>
                  <span className="font-mono">75-89 pts</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                  <span>🥉 Bronze - Sangrador Certificado</span>
                  <span className="font-mono">60-74 pts</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">{t('cpScoreCalculation')}</h4>
              <div className="p-4 bg-white rounded-lg border">
                <p className="text-sm text-gray-600 mb-2">O score final combina:</p>
                <ul className="text-sm space-y-1">
                  <li><strong>50%</strong> Score Técnico (14 critérios)</li>
                  <li><strong>50%</strong> Score Contextual (produção vs meta)</li>
                </ul>
                <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  <strong>{t('cpNoContextData')}</strong>
                  O critério usado é exibido junto ao selo no seu perfil.
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Baseado no Tripé: Justiça, Segurança, Simplicidade
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// HELPERS
// =============================================================================

const getMonthName = (month) => {
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  return months[month - 1] || '';
};

const getPerformanceColor = (percentage) => {
  if (percentage >= 110) return 'bg-yellow-500 text-white';
  if (percentage >= 100) return 'bg-green-500 text-white';
  if (percentage >= 85) return 'bg-blue-500 text-white';
  if (percentage >= 70) return 'bg-orange-500 text-white';
  return 'bg-gray-500 text-white';
};

const getProgressBarColor = (percentage) => {
  if (percentage >= 110) return 'bg-yellow-500';
  if (percentage >= 100) return 'bg-green-500';
  if (percentage >= 85) return 'bg-blue-500';
  if (percentage >= 70) return 'bg-orange-400';
  return 'bg-gray-400';
};

const getSeasonalBgColor = (status) => {
  switch (status) {
    case 'pico':
    case 'alta':
      return 'bg-green-100 text-green-800';
    case 'normal':
      return 'bg-yellow-100 text-yellow-800';
    case 'baixa':
    case 'recuperacao':
      return 'bg-orange-100 text-orange-800';
    case 'parada':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default ContextualPerformanceDashboard;
